import { GameSystemService } from "@/services";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import StudioApiService from "../../services/StudioApiService";
import {
  AsyncStatus,
  GameSystems,
  IArmyBookListItem,
  IArmyData,
  IPartnerData,
  ISavedListState,
  SortOptions
} from "../types";
import { showErrorDialog } from "./appSlice";
import { RootState } from "./store";

interface IServerList<TListItem, TItem> {
  list: TListItem[];
  listStatus: AsyncStatus;
  selectedItem: TItem;
  selectedItemStatus: AsyncStatus;
  page: number;
  reachedEnd: boolean;
  empty: boolean;
}

interface IServerCommunityBooksList extends IServerList<IArmyBookListItem, IArmyData> {
  filters: {
    unitCount: number;
    balanceValid: boolean;
    customRules: boolean;
    fanBooks: boolean;
    sortBy: SortOptions;
  };
}

interface IServerCommunityListsList extends IServerList<IArmyListListItem, IArmyList> {
  filters: {
    forceOrg: boolean;
    official: boolean;
    sortBy: SortOptions;
  };
}

interface IArmyListBase {
  id: string;
  details: {
    id: string;
    list: Omit<ISavedListState, "units">;
    armyId: string;
    armyIds: string[];
    armyName: string;
    modified: string;
    gameSystem: string;
    listPoints: number;
    armyVersions: {
      armyId: string;
      version: string;
    }[];
  };
  username: string;
  popularity: number;
  downvotes: number;
  votes: number;
  official: boolean;
}

export interface IArmyListListItem extends IArmyListBase {}

export interface IArmyList extends IArmyListBase {
  list: ISavedListState;
  voted: null | "up" | "down";
}

interface CommunityState {
  books: IServerCommunityBooksList;
  lists: IServerCommunityListsList;
  selectedPartner: {
    id: string;
    username: string;
  } | null;
}

const initialState: CommunityState = {
  selectedPartner: null,
  books: {
    list: [],
    listStatus: AsyncStatus.idle,

    selectedItem: null,
    selectedItemStatus: AsyncStatus.idle,
    page: 0,
    reachedEnd: false,
    filters: {
      unitCount: 0,
      balanceValid: false,
      customRules: true,
      fanBooks: true,
      sortBy: "votes_month"
    },
    empty: false
  },
  lists: {
    list: [],
    listStatus: AsyncStatus.idle,
    selectedItem: null,
    selectedItemStatus: AsyncStatus.idle,
    page: 0,
    reachedEnd: false,
    empty: false,
    filters: { forceOrg: false, official: false, sortBy: "votes_month" }
  }
};

//#region Books

export const getNextCommunityBooks = createAsyncThunk(
  "army/getNextCommunityBooks",
  async (query: { gameSystem: GameSystems; searchText: string; reset?: boolean }, { getState }) => {
    const state = getState() as RootState;
    const { filters, page } = state.community.books;
    const { gameSystem, searchText, reset } = query;
    const nextPage = (reset ? 0 : page) + 1;
    // AF to Web Companion game type mapping
    const slug = GameSystemService.toSlug(gameSystem);
    const res = await StudioApiService.getArmyBooks(
      slug,
      false,
      nextPage,
      searchText,
      filters.unitCount,
      filters.balanceValid,
      filters.customRules,
      filters.fanBooks,
      filters.sortBy
    );
    return {
      page: nextPage,
      reset: reset,
      books: res
    };
  }
);

export const loadCommunityBookData = createAsyncThunk(
  "army/loadCommunityBookData",
  async (payload: { armyUid: string; gameSystem: GameSystems }, { dispatch }) => {
    try {
      const armyBookData: IArmyData = await StudioApiService.getArmyBookData(
        payload.armyUid,
        payload.gameSystem
      );

      return { armyBookData };
    } catch {
      dispatch(
        showErrorDialog({
          title: "Error Loading Army Book",
          message:
            "There was an issue loading the army book. If you are trying to view a private book, ensure that you are logged in."
        })
      );
      throw "Error loading army book";
    }
  }
);

export const communityBookVote = createAsyncThunk(
  "army/communityBookVote",
  async (query: { armyBookId: string; vote: "up" | "down" }) => {
    const { armyBookId, vote } = query;
    const res = await fetch("/api/vote/book/" + armyBookId, {
      method: "POST",
      body: JSON.stringify({ vote })
    });
    return { armyBookId, vote, status: res.status };
  }
);

export const communityBookUnvote = createAsyncThunk(
  "army/communityBookUnvote",
  async (armyBookId: string) => {
    const res = await fetch("/api/vote/book/" + armyBookId, {
      method: "POST",
      body: JSON.stringify({ vote: "remove" })
    });
    return { armyBookId, status: res.status };
  }
);

//#endregion

//#region Lists

export const getNextCommunityLists = createAsyncThunk(
  "army/getNextCommunityLists",
  async (
    query: { gameSystem: GameSystems; armyId: string; searchText: string; reset?: boolean },
    { getState }
  ) => {
    const state = getState() as RootState;
    const { filters, page } = state.community.lists;
    const { gameSystem, armyId, searchText, reset } = query;
    const nextPage = (reset ? 0 : page) + 1;

    const params = new URLSearchParams({
      gameSystem: gameSystem.toString(),
      armyId,
      searchText,
      page: nextPage.toString(),
      sortBy: filters.sortBy,
      forceOrg: filters.forceOrg.toString()
    });
    const res = await fetch(`/api/community-lists?${params.toString()}`);
    const lists = await res.json();
    return {
      page: nextPage,
      reset: reset,
      lists: lists
    };
  }
);

export const getListConfigCommunityLists = createAsyncThunk(
  "army/getListConfigCommunityLists",
  async (query: { gameSystem: GameSystems; armyId: string }) => {
    const { gameSystem, armyId } = query;

    const params = new URLSearchParams({
      gameSystem: gameSystem.toString(),
      armyId
    });
    const res = await fetch(`/api/community-lists/top10?${params.toString()}`);
    const lists = await res.json();

    return {
      lists: lists
    };
  }
);

export const getCommunityList = createAsyncThunk("army/getCommunityList", async (id: string) => {
  const res = await fetch("/api/community-lists/" + id);
  const data = await res.json();
  return data as IArmyList;
});

export const communityListVote = createAsyncThunk(
  "army/communityListVote",
  async (query: { id: string; vote: "up" | "down" }) => {
    const { id, vote } = query;
    const res = await fetch("/api/vote/list/" + id, {
      method: "POST",
      body: JSON.stringify({ vote })
    });
    return { id, vote, status: res.status };
  }
);

const communityListUnvote = createAsyncThunk("army/communityListUnvote", async (id: string) => {
  const res = await fetch("/api/vote/list/" + id, {
    method: "POST",
    body: JSON.stringify({ vote: "remove" })
  });
  return { id, status: res.status };
});

//#endregion

const communitySlice = createSlice({
  name: "community",
  initialState: initialState,

  reducers: {
    resetCommunityBooks(state) {
      state.books = { ...initialState.books };
    },
    removeCommunityBook(state, action: PayloadAction<string>) {
      const bookId = action.payload;
      state.books.list = state.books.list.filter((x) => x.uid !== bookId);
    },
    setCommunityBooksFilters(
      state,
      action: PayloadAction<{
        unitCount?: number;
        balanceValid?: boolean;
        customRules?: boolean;
        fanBooks?: boolean;
        sortBy?: SortOptions;
      }>
    ) {
      const { unitCount, balanceValid, customRules, fanBooks, sortBy } = action.payload;
      if (unitCount !== undefined) state.books.filters.unitCount = unitCount;
      if (balanceValid !== undefined) state.books.filters.balanceValid = balanceValid;
      if (customRules !== undefined) state.books.filters.customRules = customRules;
      if (fanBooks !== undefined) state.books.filters.fanBooks = fanBooks;
      if (sortBy !== undefined) state.books.filters.sortBy = sortBy;

      // Clear books
      state.books.list = [];
      state.books.page = 0;
      state.books.reachedEnd = false;
    },
    setCommunityListsFilters(
      state,
      action: PayloadAction<{
        forceOrg?: boolean;
        official?: boolean;
        sortBy?: SortOptions;
      }>
    ) {
      const { forceOrg, official, sortBy } = action.payload;
      if (forceOrg !== undefined) state.lists.filters.forceOrg = forceOrg;
      if (official !== undefined) state.lists.filters.official = official;
      if (sortBy !== undefined) state.lists.filters.sortBy = sortBy;

      // Clear books
      state.lists.list = [];
      state.lists.page = 0;
      state.lists.reachedEnd = false;
    },
    deselectCommunityBook(state) {
      state.books.selectedItem = null;
    },
    selectPartner(state, action: PayloadAction<IPartnerData | null>) {
      state.selectedPartner = action.payload
        ? { id: action.payload.userId, username: action.payload.username }
        : null;
    },
    resetCommunityList(state) {
      state.lists.selectedItem = null;
    }
  },
  selectors: {
    selectSelectedCommunityBook(state) {
      return state.books.selectedItem;
    }
  },
  extraReducers(builder) {
    //#region Books
    builder.addCase(getNextCommunityBooks.fulfilled, (state, action) => {
      const { page, books, reset } = action.payload;
      return {
        ...state,
        books: {
          ...state.books,
          list: reset ? [...books] : [...state.books.list, ...books],
          page: page,
          reachedEnd: books.length < 1,
          loading: false,
          empty: (reset || page === 1) && books.length < 1
        }
      };
    });
    builder
      .addCase(loadCommunityBookData.pending, (state) => {
        state.books.selectedItemStatus = AsyncStatus.loading;
      })
      .addCase(loadCommunityBookData.rejected, (state) => {
        state.books.selectedItemStatus = AsyncStatus.failed;
      })
      .addCase(loadCommunityBookData.fulfilled, (state, action) => {
        state.books.selectedItem = action.payload.armyBookData;
        state.books.selectedItemStatus = AsyncStatus.succeeded;
      });

    builder.addCase(communityBookVote.fulfilled, (state, action) => {
      const { armyBookId, vote } = action.payload;
      const listItem = state.books.list.find((x) => x.uid === armyBookId);
      const loadedBook = state.books.selectedItem;

      // If already voted, remove this first
      if (loadedBook?.voted === "up") {
        loadedBook.popularity -= 1;
        listItem.popularity -= 1;
      } else if (loadedBook?.voted === "down") {
        loadedBook.downvotes -= 1;
        listItem.downvotes -= 1;
      }

      applyVote(listItem, vote);
      applyVote(loadedBook, vote);
    });
    builder.addCase(communityBookUnvote.fulfilled, (state, action) => {
      const { armyBookId } = action.payload;
      const listItem = state.books.list.find((x) => x.uid === armyBookId);
      const loadedBook = state.books.selectedItem;

      removeVote(listItem, loadedBook.voted);
      removeVote(loadedBook, loadedBook.voted);
    });
    //#endregion

    //#region Lists

    builder.addCase(getNextCommunityLists.fulfilled, (state, action) => {
      const { page, lists, reset } = action.payload;

      return {
        ...state,
        lists: {
          ...state.lists,
          list: reset ? [...lists] : [...state.lists.list, ...lists],
          page: page,
          reachedEnd: lists.length < 1,
          loading: false,
          empty: (reset || page === 1) && lists.length < 1
        }
      };
    });

    builder.addCase(getListConfigCommunityLists.fulfilled, (state, action) => {
      const { lists } = action.payload;

      return {
        ...state,
        lists: {
          ...state.lists,
          list: [...lists],
          page: 1,
          reachedEnd: true,
          loading: false,
          empty: false
        }
      };
    });

    builder
      .addCase(getCommunityList.pending, (state) => {
        state.lists.selectedItemStatus = AsyncStatus.loading;
      })
      .addCase(getCommunityList.fulfilled, (state, action) => {
        state.lists.selectedItem = action.payload;
        state.lists.selectedItemStatus = AsyncStatus.succeeded;
      });

    builder.addCase(communityListVote.fulfilled, (state, action) => {
      const { id, vote } = action.payload;
      const communityList = state.lists.list.find((x) => x.id === id);
      const loadedList = state.lists.selectedItem;

      // If already voted, remove this first
      if (loadedList?.voted === "up") {
        loadedList.popularity -= 1;
        communityList.popularity -= 1;
      } else if (loadedList?.voted === "down") {
        loadedList.downvotes -= 1;
        communityList.downvotes -= 1;
      }

      applyVote(communityList, vote);
      applyVote(loadedList, vote);
    });

    builder.addCase(communityListUnvote.fulfilled, (state, action) => {
      const { id } = action.payload;
      const communityList = state.lists.list.find((x) => x.id === id);
      const loadedList = state.lists.selectedItem;

      removeVote(communityList, loadedList.voted);
      removeVote(loadedList, loadedList.voted);
    });

    //#endregion
  }
});

const applyVote = (entity, vote) => {
  if (entity) {
    const downvote = vote === "down";
    entity.popularity += downvote ? 0 : 1;
    entity.downvotes += downvote ? 1 : 0;
    entity.voted = vote;
  }
};

const removeVote = (entity, vote) => {
  if (entity) {
    const downvote = vote === "down";
    entity.popularity -= downvote ? 0 : 1;
    entity.downvotes -= downvote ? 1 : 0;
    entity.voted = null;
  }
};

// Action creators are generated for each case reducer function
export const {
  removeCommunityBook,
  resetCommunityBooks,
  setCommunityBooksFilters,
  setCommunityListsFilters,
  deselectCommunityBook,
  selectPartner,
  resetCommunityList
} = communitySlice.actions;

export const { selectSelectedCommunityBook } = communitySlice.selectors;

export default communitySlice.reducer;
